import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────

export type Workspace = {
  id: string;
  owner_user_id: string;
  name: string;
  created_at: string;
};

export type WorkspaceMember = {
  id: string;
  workspace_id: string;
  user_id: string | null;
  invited_email: string;
  invited_at: string;
  joined_at: string | null;
  removed_at: string | null;
  removed_reason: string | null;
};

// ── Hooks ─────────────────────────────────────────────────────

/**
 * Check if the current user is an active workspace member.
 * Returns `removed: true` if user was removed from all workspaces.
 * Used by ProtectedRoute to block access for ex-members.
 */
export function useWorkspaceMembership() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["workspace-membership", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workspace_members" as any)
        .select("id, workspace_id, removed_at, removed_reason")
        .eq("user_id", user!.id)
        .is("removed_at", null)
        .limit(1);

      if (error) throw error;

      // If no active membership found, check if they were removed
      if (!data || data.length === 0) {
        const { data: removedData } = await supabase
          .from("workspace_members" as any)
          .select("removed_at, removed_reason")
          .eq("user_id", user!.id)
          .not("removed_at", "is", null)
          .order("removed_at", { ascending: false })
          .limit(1);

        if (removedData && removedData.length > 0) {
          return {
            isActive: false,
            removed: true,
            removedAt: removedData[0].removed_at as string,
            removedReason: removedData[0].removed_reason as string | null,
          };
        }

        // No membership at all (shouldn't happen — trigger creates it)
        return { isActive: false, removed: false, removedAt: null, removedReason: null };
      }

      return { isActive: true, removed: false, removedAt: null, removedReason: null };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });
}

/** Current user's active workspace */
export function useWorkspace() {
  const { user } = useAuth();

  const workspaceQuery = useQuery({
    queryKey: ["workspace", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workspaces" as any)
        .select("*")
        .limit(1)
        .single();
      if (error) throw error;
      return data as Workspace;
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });

  const isOwner = useMemo(
    () => workspaceQuery.data?.owner_user_id === user?.id,
    [workspaceQuery.data, user?.id],
  );

  return { workspace: workspaceQuery.data, isOwner, ...workspaceQuery };
}

/** Members of the current workspace (active only, excludes removed) */
export function useWorkspaceMembers() {
  const { workspace } = useWorkspace();

  return useQuery({
    queryKey: ["workspace-members", workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("workspace_members" as any)
        .select("*")
        .eq("workspace_id", workspace!.id)
        .is("removed_at", null)
        .order("invited_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as WorkspaceMember[];
    },
    enabled: !!workspace?.id,
    staleTime: 2 * 60 * 1000,
  });
}

/** Invite a member to the workspace by Google email */
export function useInviteMember() {
  const qc = useQueryClient();
  const { workspace } = useWorkspace();

  return useMutation({
    mutationFn: async (email: string) => {
      if (!workspace) throw new Error("Workspace não encontrado");

      // Check if already a member (active or removed)
      const { data: existing } = await supabase
        .from("workspace_members" as any)
        .select("id, removed_at")
        .eq("workspace_id", workspace.id)
        .eq("invited_email", email.toLowerCase().trim())
        .maybeSingle();

      if (existing && !existing.removed_at) {
        throw new Error("Este e-mail já é membro do seu workspace");
      }

      if (existing && existing.removed_at) {
        // Re-invite previously removed member
        const { error } = await supabase
          .from("workspace_members" as any)
          .update({
            removed_at: null,
            removed_reason: null,
            invited_at: new Date().toISOString(),
            joined_at: null,
            user_id: null,
          })
          .eq("id", existing.id);
        if (error) throw error;
        return;
      }

      // New invitation
      const { error } = await supabase
        .from("workspace_members" as any)
        .insert({
          workspace_id: workspace.id,
          invited_email: email.toLowerCase().trim(),
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-members"] });
      toast.success("Membro convidado com sucesso");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Erro ao convidar membro");
    },
  });
}

/** Remove a member from the workspace (soft delete) */
export function useRemoveMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, reason }: { memberId: string; reason?: string }) => {
      const { error } = await supabase
        .from("workspace_members" as any)
        .update({
          removed_at: new Date().toISOString(),
          removed_reason: reason || "owner_removed",
        })
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-members"] });
      toast.success("Membro removido do workspace");
    },
    onError: () => {
      toast.error("Erro ao remover membro");
    },
  });
}
